import axios from "axios"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope"
import type {
  OpenedUploadLink,
  UploadRefusal,
  UploadResponse,
  UploadResult
} from "../types"
import { customerUploadClient } from "./customer-upload.client"

const ROOT = "/calendars/customer-upload"

const asResponse = <T>(status: number, body: unknown): UploadResponse<T> =>
  status >= 200 && status < 300
    ? { ok: true, status, data: unwrapEnvelope<T>(body), refusal: null }
    : {
        ok: false,
        status,
        data: null,
        refusal: (body ?? { reason: "unknown" }) as UploadRefusal
      }

// `validateStatus: status < 500` only covers what the client resolves; a 5xx or
// a network failure rejects the promise instead. Every caller gets to assume
// one response shape regardless — a page stuck on a spinner/"Sending…" forever
// is the cost of not catching this here.
// A 503 from a scanner outage still carries the server's own reason and words.
const isRefusal = (body: unknown): body is UploadRefusal =>
  typeof body === "object" &&
  body !== null &&
  typeof (body as UploadRefusal).reason === "string"

const asFailureResponse = <T>(error: unknown): UploadResponse<T> => {
  const response = axios.isAxiosError(error) ? error.response : undefined
  return {
    ok: false,
    status: response ? response.status : 0,
    data: null,
    refusal: isRefusal(response?.data) ? response.data : { reason: "unknown" }
  }
}

/** The token goes in a body, never a URL: paths and queries end up in logs. */
export const openUploadLink = async (token: string) => {
  try {
    const { status, data } = await customerUploadClient.post(`${ROOT}/open/`, {
      token
    })
    return asResponse<OpenedUploadLink>(status, data)
  } catch (error) {
    return asFailureResponse<OpenedUploadLink>(error)
  }
}

export const uploadCustomerFile = async (
  token: string,
  file: File,
  onProgress?: (percent: number) => void
) => {
  const body = new FormData()
  body.append("token", token)
  body.append("files", file)
  try {
    const { status, data } = await customerUploadClient.post(
      `${ROOT}/files/`,
      body,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (onProgress && event.total)
            onProgress(Math.round((event.loaded / event.total) * 100))
        }
      }
    )
    return asResponse<UploadResult>(status, data)
  } catch (error) {
    return asFailureResponse<UploadResult>(error)
  }
}
